import { useEffect, useState } from "react";
import { EnvelopeSimple, MapPin, Phone, PaperPlaneRight } from "@phosphor-icons/react";
import { submitLead } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { toast } from "sonner";

export default function Contact() {
    useEffect(() => {
        document.title = "Surveys2CAD | Contact";
    }, []);

    const [form, setForm] = useState({
        name: "", email: "", phone: "", company: "", state: "", message: "", service_type: "",
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead({ ...form, source: "contact" });
            toast.success("Message received. We'll reply within 1 business hour.");
            setForm({ name: "", email: "", phone: "", company: "", state: "", message: "", service_type: "" });
        } catch {
            toast.error("Couldn't send — please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <PageHero variant="contact" tint="emerald">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-24">
                    <div className="max-w-3xl">
                        <div className="overline mb-3">Contact</div>
                        <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-50 tracking-tighter leading-[1.05]">
                            Let's talk about <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(120deg, #34d399 0%, #22d3ee 50%, #fb923c 100%)" }}
                            >
                                your project.
                            </span>
                        </h1>
                        <p className="text-slate-400 mt-6 text-lg leading-relaxed">
                            Whether you need a single boundary survey drafted by tomorrow, or a long-term drafting partnership —
                            we reply within one business hour.
                        </p>
                    </div>
                </div>
            </PageHero>

            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 md:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <form onSubmit={submit} className="lg:col-span-3 tech-card p-6 md:p-8" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                        <Field label="Work email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
                        <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
                        <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="contact-state" />
                        <Field label="Service of interest" value={form.service_type} onChange={(v) => setForm({ ...form, service_type: v })} testid="contact-service" />
                    </div>
                    <div className="mt-4">
                        <label className="overline mb-2 block">Project details</label>
                        <textarea
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            rows={5}
                            className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-none"
                            placeholder="Tell us about acreage, deliverables, field data, timeline…"
                            data-testid="contact-message"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary mt-5" data-testid="contact-submit-btn">
                        {loading ? "Sending…" : "Send message"}
                        <PaperPlaneRight size={16} weight="bold" />
                    </button>
                    <p className="text-xs text-slate-500 mt-3">
                        We typically respond within 1 business hour. All inquiries covered by NDA on request.
                    </p>
                </form>

                <aside className="lg:col-span-2 space-y-6">
                    <InfoCard icon={EnvelopeSimple} title="Email" value="surveys2cad@gmail.com" />
                    <InfoCard icon={Phone} title="Phone" value="Available on request" />
                    <InfoCard icon={MapPin} title="Coverage" value="24 Northern US States · Remote delivery worldwide" />
                    <div className="tech-card p-6">
                        <div className="overline mb-3">Response windows</div>
                        <ul className="text-sm text-slate-300 space-y-2">
                            <li className="flex justify-between"><span>Quote request</span><span className="text-cyan-400 font-mono-alt">&lt; 1 hr</span></li>
                            <li className="flex justify-between"><span>Standard drafting</span><span className="text-cyan-400 font-mono-alt">48–72 hrs</span></li>
                            <li className="flex justify-between"><span>Rush delivery</span><span className="text-orange-400 font-mono-alt">24 hrs</span></li>
                            <li className="flex justify-between"><span>Revisions</span><span className="text-cyan-400 font-mono-alt">Same day</span></li>
                        </ul>
                    </div>
                </aside>
            </div>
            </div>
        </div>
    );
}

function Field({ label, type = "text", required, value, onChange, testid }) {
    return (
        <div>
            <label className="overline mb-2 block">{label}{required && <span className="text-orange-400 ml-1">*</span>}</label>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                data-testid={testid}
            />
        </div>
    );
}

function InfoCard({ icon: Icon, title, value }) {
    return (
        <div className="tech-card p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-500/10 border border-cyan-500/30 flex-shrink-0">
                <Icon size={18} weight="duotone" className="text-cyan-300" />
            </div>
            <div>
                <div className="overline">{title}</div>
                <div className="text-slate-100 mt-1">{value}</div>
            </div>
        </div>
    );
}
